import { defineStore } from 'pinia'
import client from '@/api/client'
import { ref, nextTick, markRaw } from 'vue'
import { fallbackLens, getDefaultLens } from '@/includes/lens'

// Shown when a lens preview image fails to load, so the video/spinner UI never hangs waiting on a broken URL
const FALLBACK_PREVIEW =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="225">' +
      '<rect width="100%" height="100%" fill="#1a1a1a"/>' +
      '<text x="50%" y="50%" fill="#888" font-family="sans-serif" font-size="14" text-anchor="middle" dominant-baseline="middle">Preview unavailable</text>' +
      '</svg>'
  )

// A /lens request started ahead of the page that needs it (see prefetch)
let prefetched = null
const PREFETCH_TTL = 10000

const sortLenses = lenses => {
  return lenses.sort((a, b) => {
    const focalLengthA = a.focalLength ? parseFloat(a.focalLength.replace('mm', '')) : 0
    const focalLengthB = b.focalLength ? parseFloat(b.focalLength.replace('mm', '')) : 0
    const transmissionA = a.transmission
      ? parseFloat(a.transmission.toString().replace(/[^0-9.]/g, ''))
      : 0
    const transmissionB = b.transmission
      ? parseFloat(b.transmission.toString().replace(/[^0-9.]/g, ''))
      : 0
    const focalDiff = focalLengthA - focalLengthB
    if (focalDiff !== 0) {
      return focalDiff
    }
    return transmissionA - transmissionB
  })
}

export const useLensesStore = defineStore('lenses', {
  state: () => ({
    base_URL: process.env.VUE_APP_BASE_URL_V2,
    data: [],
    datafetched: false,
    lensesName: [],
    LeftSelectedVideoId: null,
    RightSelectedVideoId: null,
    defaultLeftVideoId: null,
    defaultRightVideoId: null,
    defaultMobileVideoId: null,
    LeftSelectedVideoDetails: null,
    RightSelectedVideoDetails: null,
    SelectedVideo: null,
    LeftFocalLength: null,
    RightFocalLength: null,
    LeftTransmission: null,
    RightTransmission: null,
    LeftVideoLoaded: false,
    RightVideoLoaded: false,
    focalLengthOptionsLeft: [],
    focalLengthOptionsRight: [],
    transmissionsLeft: [],
    transmissionsRight: [],
    videoControlBtn: 'play',
    playVideo: false,
    pauseVideo: false,
    isPremiumUser: false,
    //states for mobile version selected video
    SelectedVideoDetails: null,
    SelectedFocalLength: null,
    SelectedTransmission: null,
    debounceTimer: null,
    searchIsActive: false,
    video1Ref: ref(null),
    video2Ref: ref(null),
    isProgrammaticScroll: false,
    programmaticScrollTimer: null
  }),
  getters: {
    getLeftSelectedVideoId: state => state.LeftSelectedVideoId,
    getRightSelectedVideoId: state => state.RightSelectedVideoId,
    getLeftFocalLength: state => state.LeftFocalLength,
    getRightFocalLength: state => state.RightFocalLength,
    getleftTransmission: state => state.LeftTransmission,
    getRightTransmission: state => state.RightTransmission,
    getLeftVideoLoaded: state => state.LeftVideoLoaded,
    getRightVideoLoaded: state => state.RightVideoLoaded,
    getLensesdata: state => state.data,
    getFilteredLenses: state => state.filteredLenses,
    // Names of lenses with at least one premium variation. Built once per data
    // load so per-lens lookups are O(1) instead of rescanning every category.
    premiumLensNames: state => {
      const names = new Set()
      state.data.forEach(category =>
        category.lenses?.forEach(lens => {
          if (lens.isPremium === true) names.add(lens.name)
        })
      )
      return names
    },
    // A lens (by name) is premium if any of its variations is flagged premium.
    isLensPremium() {
      const premiumNames = this.premiumLensNames
      return name => premiumNames.has(name)
    },
    // Per-category list of unique lens names (free first, then premium, each
    // alphabetical), shared by both sidebars so it is computed once, not per render.
    sidebarLenses() {
      const premiumNames = this.premiumLensNames
      return this.data.map(category => {
        const byName = new Map()
        category.lenses?.forEach(lens => {
          if (!byName.has(lens.name) || lens.isNew) byName.set(lens.name, lens.isNew)
        })
        const lenses = [...byName].map(([name, isNew]) => ({
          name,
          isNew,
          isPremium: premiumNames.has(name)
        }))
        lenses.sort((a, b) => {
          if (a.isPremium !== b.isPremium) return a.isPremium ? 1 : -1
          return a.name.localeCompare(b.name)
        })
        return { id: category.id, name: category.name, lenses }
      })
    }
  },
  actions: {
    // Starts the /lens request early (e.g. from the router guard, in parallel with
    // the profile request) so the page doesn't wait on a serial request waterfall.
    // The next fetchData() call picks the result up instead of requesting again.
    prefetch() {
      if (prefetched && Date.now() - prefetched.at < PREFETCH_TTL) return
      prefetched = { at: Date.now(), promise: this.fetchData(true) }
    },
    async fetchData(isPrefetch = false) {
      if (!isPrefetch && prefetched) {
        const { at, promise } = prefetched
        prefetched = null
        if (Date.now() - at < PREFETCH_TTL) return promise
      }
      try {
        const API_URL = `${this.base_URL}/lens`
        const response = await client.get(API_URL)
        // Read-only after load, so skip deep reactive proxying of every lens
        this.data = markRaw(response.data?.lens || [])
        this.isPremiumUser = response.data?.isPremiumUser
        this.setDataFetched()
        this.setDefaultLeftVideo()
        this.setDefaultRightVideo()
        this.setDefaultMobileVideo()
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    },
    setDataFetched() {
      this.datafetched = true
    },
    setDefaultLeftVideo() {
      this.defaultLeftVideoId = getDefaultLens('Left', this.data)
    },
    setDefaultRightVideo() {
      this.defaultRightVideoId = getDefaultLens('Right', this.data)
    },
    setDefaultMobileVideo() {
      this.defaultMobileVideoId = getDefaultLens(null, this.data)
    },
    setLeftSelectedVideoId(LeftVideoID) {
      this.LeftSelectedVideoId = LeftVideoID
    },
    setRightSelectedVideoId(RightVideoID) {
      this.RightSelectedVideoId = RightVideoID
    },
    setLeftFocalLength(LeftFocalLength) {
      this.LeftFocalLength = LeftFocalLength
    },
    setRightFocalLength(RightFocalLength) {
      this.RightFocalLength = RightFocalLength
    },
    setLeftTransmission(LeftTransmission) {
      this.LeftTransmission = LeftTransmission
    },
    setRightTransmission(RightTransmission) {
      this.RightTransmission = RightTransmission
    },
    toggleLeftVideoLoaded() {
      this.LeftVideoLoaded = !this.LeftVideoLoaded
    },
    toggleRightVideoLoaded() {
      this.RightVideoLoaded = !this.RightVideoLoaded
    },
    setSelectedVideo(lenseName) {
      this.SelectedVideo = lenseName
    },
    setSelectedFocalLength(focalLength) {
      this.SelectedFocalLength = focalLength
    },
    setSelectedTransmission(transmission) {
      this.SelectedTransmission = transmission
    },
    setLeftSelectedVideoDetails() {
      const defaultArray = this.data.map(d => d.lenses)
      const flatArray = defaultArray.flat()

      const middleObject = flatArray.filter(
        fa => fa.name.toLowerCase().replace(/\s+/g, '-') + '-' + 'left' === this.LeftSelectedVideoId
      )

      this.LeftSelectedVideoDetails = sortLenses(middleObject)
    },
    setRightSelectedVideoDetails() {
      const defaultArray = this.data.map(d => d.lenses)
      const flatArray = defaultArray.flat()

      const middleObject = flatArray.filter(
        fa =>
          fa.name.toLowerCase().replace(/\s+/g, '-') + '-' + 'right' === this.RightSelectedVideoId
      )
      this.RightSelectedVideoDetails = sortLenses(middleObject)
    },
    setSelectedVideoDetails() {
      const defaultArray = this.data.map(d => d.lenses)
      const flatArray = defaultArray.flat()

      const middleObject = flatArray.filter(
        fa => fa.name.toLowerCase().replace(/\s+/g, '-') === this.SelectedVideo
      )
      this.SelectedVideoDetails = sortLenses(middleObject)
    },
    generateVideoLabelText(label, target) {
      label.value.textContent =
        target === 'LEFT'
          ? this.LeftSelectedVideoDetails[0].name
          : this.RightSelectedVideoDetails[0].name
    },
    removeDuplicates(arr) {
      return arr.filter((item, index, self) => {
        return index === self.findIndex(t => t.focalLength === item.focalLength)
      })
    },
    generateTransmissionDropdown(array, focalLength) {
      return array.filter(transmission => transmission.focalLength === focalLength)
    },
    async generateDropDown(target, focalLengthElement, transmissionElement) {
      const tempArray =
        target === 'LEFT'
          ? this.LeftSelectedVideoDetails
          : target === 'RIGHT'
            ? this.RightSelectedVideoDetails
            : this.SelectedVideoDetails

      const pinnedFocalLength =
        target === 'LEFT'
          ? this.LeftFocalLength
          : target === 'RIGHT'
            ? this.RightFocalLength
            : this.SelectedFocalLength

      let focalLengths = await this.removeDuplicates(tempArray)

      // Keep whichever focal length is actually selected (e.g. pinned by a
      // keyword search) instead of always building the dropdown around the
      // first variant in the list
      const activeFocalLength = focalLengths.some(
        option => option.focalLength === pinnedFocalLength
      )
        ? pinnedFocalLength
        : focalLengths[0].focalLength

      const transmission = await this.generateTransmissionDropdown(tempArray, activeFocalLength)

      // Replace (rather than append to) the option lists driving the v-for -
      // pushing onto them left every previous selection's options in the DOM
      if (target === 'LEFT') {
        this.focalLengthOptionsLeft = focalLengths.map(option => option.focalLength)
        this.transmissionsLeft = transmission.map(option => option.transmission)
      } else {
        this.focalLengthOptionsRight = focalLengths.map(option => option.focalLength)
        this.transmissionsRight = transmission.map(option => option.transmission)
      }

      focalLengthElement.value.style.display = ''
      transmissionElement.value.style.display = ''

      const pinnedTransmission =
        target === 'LEFT'
          ? this.LeftTransmission
          : target === 'RIGHT'
            ? this.RightTransmission
            : this.SelectedTransmission
      const activeTransmission = transmission.some(
        option => option.transmission === pinnedTransmission
      )
        ? pinnedTransmission
        : transmission[0]?.transmission

      // Wait for Vue to render the <option> elements from the arrays above
      // before setting .value - otherwise the select still has no options
      // and the browser silently falls back to selecting the first one
      await nextTick()
      focalLengthElement.value.value = activeFocalLength
      transmissionElement.value.value = activeTransmission
    },
    generateIcon(icon, iconElem) {
      if (!icon || !iconElem) return
      iconElem.value.src = icon
      iconElem.value.style.display = 'block'
    },
    loadDefaultVideoPath(spinnerRef, videoRef, target) {
      spinnerRef.value.style.display = 'flex'
      const sourceElement = videoRef.value.querySelector('source')
      const item =
        target === 'LEFT'
          ? this.LeftSelectedVideoDetails
          : target === 'RIGHT'
            ? this.RightSelectedVideoDetails
            : this.SelectedVideoDetails
      sourceElement.src = item[0].videoPath
      videoRef.value.poster = item[0].preview
      const posterImage = new Image()
      posterImage.src = item[0].preview // Specify the path to your poster image
      posterImage.onload = () => {
        // Poster image has fully loaded and displayed, hide the spinner
        if (spinnerRef.value) spinnerRef.value.style.display = 'none'
      }
      posterImage.onerror = () => {
        // Broken/unreachable preview shouldn't hang the spinner or block video playback
        console.warn(`Lens preview failed to load: ${item[0].preview}`)
        if (spinnerRef.value) spinnerRef.value.style.display = 'none'
        if (videoRef.value) videoRef.value.poster = FALLBACK_PREVIEW
      }

      videoRef.value.load()
    },
    setVideoPathOnChange(lensesItem, videoRef) {
      if (!lensesItem) return

      videoRef.value.poster = lensesItem.preview
      const sourceElement = videoRef.value.querySelector('source')
      sourceElement.src = lensesItem.videoPath
      videoRef.value.load()
    },
    enterFullscreen(videoRef) {
      if (videoRef.value.requestFullscreen) {
        videoRef.value.requestFullscreen()
      } else if (videoRef.value.mozRequestFullScreen) {
        // Firefox
        videoRef.value.mozRequestFullScreen()
      } else if (videoRef.value.webkitRequestFullscreen) {
        // Chrome, Safari, and Opera
        videoRef.value.webkitRequestFullscreen()
      } else if (videoRef.value.msRequestFullscreen) {
        // IE/Edge
        videoRef.value.msRequestFullscreen()
      } else if (videoRef.value.webkitEnterFullscreen) {
        videoRef.value.webkitEnterFullscreen() //for iphone this code worked
      }
    },

    setPlayVideo() {
      this.playVideo = true
      this.pauseVideo = false
      this.videoControlBtn = 'pause'
    },
    setPauseVideo() {
      this.pauseVideo = true
      this.playVideo = false
      this.videoControlBtn = 'play'
    },

    debounce(callback, time) {
      window.clearTimeout(this.debounceTimer)
      this.debounceTimer = window.setTimeout(callback, time)
    },
    getElementLayoutTop(element, container) {
      let elementTop = 0
      let current = element
      while (current) {
        elementTop += current.offsetTop || 0
        current = current.offsetParent
      }

      let containerTop = 0
      current = container
      while (current) {
        containerTop += current.offsetTop || 0
        current = current.offsetParent
      }

      return elementTop - containerTop
    },
    snapToClosestOption(element, isMobile) {
      if (!element) return
      const viewportList = element
      const viewportFromTop = viewportList.getBoundingClientRect().top

      const marginTopOfViewPort =
        parseInt(
          window.getComputedStyle(viewportList).getPropertyValue('margin-top').replace('px', '')
        ) || 0

      const listItemsLink = viewportList.querySelectorAll('.nav-link')
      const viewportListHeightHalf = viewportList.getBoundingClientRect().height / 2
      const viewportListCenter = viewportFromTop + viewportListHeightHalf - marginTopOfViewPort + 6 // 6 is added as the padding and margin of the list assuming it is static

      // 3d effect (always updated for visual feedback)
      this.scroll3dEffect(viewportListCenter, isMobile)

      // Prevent snap selection change during programmatic scroll to prevent feedback loop
      if (this.isProgrammaticScroll) {
        return
      }

      const listItemsLi = []
      listItemsLink.forEach(link => {
        const li = link.closest('.nav-item')
        if (li) listItemsLi.push(li)
      })

      if (listItemsLi.length === 0) return

      // Read every position first, then write, so the browser lays out once
      // instead of once per item
      const layoutCenter = viewportList.scrollTop + viewportList.clientHeight / 2
      let closestLi = listItemsLi[0]
      let closestDiff = Infinity

      listItemsLi.forEach(item => {
        const itemCenter = this.getElementLayoutTop(item, viewportList) + item.clientHeight / 2
        const currentDiff = Math.abs(layoutCenter - itemCenter)
        if (currentDiff < closestDiff) {
          closestDiff = currentDiff
          closestLi = item
        }
      })

      listItemsLi.forEach(item => item.classList.remove('active-li'))

      let defaultLeft, defaultRight, defaultVideo
      if (!isMobile) {
        defaultLeft = document.getElementById(this.defaultLeftVideoId)

        defaultRight = document.getElementById(this.defaultRightVideoId)

        if (!defaultLeft) defaultLeft = document.getElementById(fallbackLens('Left', this.data))
        if (!defaultRight) defaultRight = document.getElementById(fallbackLens('Right', this.data))
      } else {
        defaultVideo = document.getElementById(this.defaultMobileVideoId)
        if (!defaultVideo) defaultVideo = document.getElementById(fallbackLens(null, this.data))
      }

      if (!isMobile) {
        if (defaultLeft) defaultLeft.classList.remove('active-li')
        if (defaultRight) defaultRight.classList.remove('active-li')
      } else {
        if (defaultVideo) defaultVideo.classList.remove('active-li')
      }

      //adds the active class where the font is bold
      closestLi.classList.add('active-li')
      const anchorElem = closestLi.querySelector('a')

      //check if the anchor text is already clicked
      if (anchorElem.getAttribute('data-clicked') === 'true') {
        return
      } else {
        // store.setLeftSelectedVideoId(anchorElem.getAttribute('id'))
        this.debounce(() => {
          if (!isMobile) {
            viewportList.getAttribute('id') === 'viewportListLeft'
              ? this.setLeftSelectedVideoId(anchorElem.getAttribute('id'))
              : this.setRightSelectedVideoId(anchorElem.getAttribute('id'))
          } else {
            this.setSelectedVideo(anchorElem.getAttribute('id'))
          }
        }, 700)
      }
    },
    scrollToItem(item, behavior = 'smooth') {
      if (!item) return
      this.isProgrammaticScroll = true

      const li = item.parentElement
      const scrollableList = li.parentElement.parentElement.parentElement

      // Immediately highlight target and un-highlight others to avoid lag/flicker
      scrollableList.querySelectorAll('.nav-item').forEach(el => {
        el.classList.remove('active-li')
      })
      li.classList.add('active-li')

      // Use layout-based offset calculations to center the item precisely, unaffected by 3D transforms
      const itemLayoutTop = this.getElementLayoutTop(li, scrollableList)
      const scrollTo = itemLayoutTop - scrollableList.clientHeight / 2 + li.clientHeight / 2

      scrollableList.scrollTo({
        top: scrollTo,
        behavior
      })

      window.clearTimeout(this.programmaticScrollTimer)
      this.programmaticScrollTimer = window.setTimeout(() => {
        this.isProgrammaticScroll = false
      }, 1000)
    },
    addHeightOntopAndBottom(element) {
      const viewportList = element.value
      if (!viewportList) {
        return
      }
      const lists = viewportList.querySelectorAll('li')
      const clientHeight = viewportList.clientHeight
      const firstLink = document.querySelectorAll('.nav-link')[0]
      const firstNavTitle = document.querySelectorAll('.nav-title')[0]
      // Nothing rendered yet (e.g. no lenses returned) - nothing to pad around
      if (!lists.length || !firstLink || !firstNavTitle) return

      const lastListHeight = lists[lists.length - 1].clientHeight

      // is mobile
      if (viewportList.classList.contains('viewport')) {
        // substracting the paddings and margins of the nested uls and lis assuming the structure and the values wont change
        viewportList.style.paddingTop = `${
          clientHeight / 2 -
          2 -
          firstNavTitle.clientHeight -
          2 -
          4 -
          2 - // 16 for the margin top of the nav-title added on css
          firstLink.clientHeight / 2
        }px`
      } else {
        viewportList.style.paddingTop = `${
          clientHeight / 2 -
          2 -
          firstNavTitle.clientHeight -
          2 -
          4 -
          2 -
          10 - // 10 for the margin top of the nav-title added on css
          firstLink.clientHeight / 2
        }px`
      }

      // substracting the paddings and margins of the nested uls and lis assuming the structure and the values wont change
      viewportList.style.paddingBottom = `${clientHeight / 2 - 2 - 2 - 4 - lastListHeight / 2}px`
    },
    scroll3dEffect(viewportListCenter, isMobile) {
      const navEntities = document.querySelectorAll('.nav-entity')

      // Batch all layout reads ahead of the style writes to avoid layout thrashing
      const bases = Array.from(navEntities, item => {
        const base = Math.min(item.getBoundingClientRect().top - viewportListCenter, 180)
        return Math.max(base, -180)
      })

      navEntities.forEach((item, i) => {
        const base = bases[i]
        item.style.transform = `rotateX(${base * 0.6}deg)`
        item.style.transformOrigin = base > 0 ? 'top' : 'bottom'
        if (isMobile) {
          item.style.opacity = 1 - Math.abs(base) * 0.01
        }
      })
    },
    setVideo1Ref(ref) {
      this.video1Ref = ref
    },

    setVideo2Ref(ref) {
      this.video2Ref = ref
    },

    playBothVideos() {
      if (this.video1Ref && this.video2Ref) {
        this.video1Ref.play()
        this.video2Ref.play()
        this.setPlayVideo()
      }
    },

    pauseBothVideos() {
      if (this.video1Ref && this.video2Ref) {
        this.video1Ref.pause()
        this.video2Ref.pause()
        this.setPauseVideo()
      }
    },

    skipBothVideos(direction, playBackSpeed = 1) {
      if (!this.video1Ref || !this.video2Ref) return

      if (direction === 'forward') {
        if (
          this.video1Ref.currentTime < this.video1Ref.duration &&
          this.video2Ref.currentTime < this.video2Ref.duration
        ) {
          this.video1Ref.currentTime = Math.min(
            this.video1Ref.currentTime + playBackSpeed,
            this.video1Ref.duration
          )
          this.video2Ref.currentTime = Math.min(
            this.video2Ref.currentTime + playBackSpeed,
            this.video2Ref.duration
          )
        }
      } else if (direction === 'backward') {
        if (this.video1Ref.currentTime > 0 && this.video2Ref.currentTime > 0) {
          this.video1Ref.currentTime = Math.max(this.video1Ref.currentTime - playBackSpeed, 0)
          this.video2Ref.currentTime = Math.max(this.video2Ref.currentTime - playBackSpeed, 0)
        }
      }
    },

    syncVideos(sourceVideo) {
      if (!this.video1Ref || !this.video2Ref) return

      const currentTime =
        sourceVideo === 'video1' ? this.video1Ref.currentTime : this.video2Ref.currentTime

      this.video1Ref.pause()
      this.video2Ref.pause()
      this.video1Ref.currentTime = currentTime
      this.video2Ref.currentTime = currentTime
    }
  }
})
